import securityAuditLogStyles from './security-audit-log.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type {
  SecurityAuditLogItem,
  DataTableFilterDescriptor as ApiFilterDescriptor,
} from '../../api/agwa-api-model';
import { ApiSecurityAudit } from '../../api/core/api-security-audit';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { keptTextPlain } from '../../language/kept-text';
import { personIdsIn } from '../../language/person-tag';
import { userDirectoryInstance } from '../../global-data/user-directory-instance';
import './security-audit-change-line';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '../../components/search/agwa-search';
import {
  isSelectionEmpty,
  selectedValues,
  type FilterFacet,
  type FilterSelection,
} from '../../components/filter/filter-facets';
import { SEARCH_CLAUSE_KEY } from '../../components/filter/agwa-filter-summary';
import '../../components/filter/agwa-filter-rail';
import '../../components/filter/agwa-filter-summary';
import '../../components/layout/agwa-fullscreen-block';
import '../../components/empty-state/agwa-empty-state';
import '../../components/data-table/data-table';
import { styles as sharedStyles } from '../../styles/shared-styles';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { terms } from '../../language/terms';
import { auditCategoryLabel } from './security-audit-category-label';
import '../../components/user/username';

import type {
  DataTableColumnDef,
  DataTable,
} from '../../components/data-table/data-table';
import type {
  DataTableDataProvider,
  DataTableFilterDescriptor,
} from '../../components/data-table/data-table-provider';

/** Facet keys are the server's filter field names, so a tick maps straight to a descriptor. */
const FACET_CATEGORY = 'category';

const FACET_ACTIVITY = 'activity';
const FACET_ACTION = 'actionType';

/**
 * Security Audit Log viewer component.
 * Displays security audit logs with filtering, sorting, and pagination.
 * Only accessible to AGWA staff with MemberAdmin rights.
 */
@customElement('security-audit-log')
export class SecurityAuditLog extends WithUserProfileConsumer(LitElement) {
  @state() private auditProvider?: DataTableDataProvider<SecurityAuditLogItem>;
  @state() private error?: string;
  @state() private hasItems = false;
  @state() private hasFetched = false;

  /**
   * The rail's ticks, keyed by facet; empty means nothing is narrowing. Each facet is sent
   * as one `eq` descriptor per ticked value, which the job reads as alternatives.
   */
  @state() private filterSelection: FilterSelection = {};
  @state() private searchText = '';
  /** The matched count the last first-page fetch reported, for the sentence above the rows. */
  @state() private matchedCount: number | null = null;

  static styles = [sharedStyles, unsafeCSS(securityAuditLogStyles)];

  private get auditColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Date',
        fieldName: 'createdUtc',
        width: '180px',
        sortable: true,
        fixed: 'left',
        displayValue: (_table: DataTable, item: SecurityAuditLogItem) =>
          item.createdUtc
            ? html`<sl-format-date
                date=${item.createdUtc}
                month="short"
                day="numeric"
                year="numeric"
                hour="2-digit"
                minute="2-digit"
              ></sl-format-date>`
            : html``,
      },
      {
        title: terms.loginUser.value,
        fieldName: 'performedByUsername',
        width: '160px',
        sortable: true,
        // The person named by id through the directory, never by the username the row also
        // carries (D-151, contact rule 8). An entry with no performer was the system's own act.
        displayValue: (_table: DataTable, item: SecurityAuditLogItem) =>
          item.performedByUserId
            ? html`<user-name .userId=${item.performedByUserId}></user-name>`
            : html`System`,
      },
      {
        title: 'Activity',
        fieldName: 'activity',
        width: '160px',
        sortable: true,
        displayValue: (_table: DataTable, item: SecurityAuditLogItem) =>
          html`<sl-badge variant="neutral" pill>${item.activity}</sl-badge>`,
      },
      {
        title: 'Category',
        fieldName: 'category',
        width: '100px',
        sortable: true,
        displayValue: (_table: DataTable, item: SecurityAuditLogItem) =>
          this.renderCategoryBadge(item.category),
      },
      {
        title: 'Action',
        fieldName: 'actionType',
        width: '100px',
        sortable: true,
        displayValue: (_table: DataTable, item: SecurityAuditLogItem) =>
          this.renderActionBadge(item.actionType),
      },
      {
        title: 'Description',
        fieldName: 'description',
        width: '300px',
        // The server writes {{term ...}} and {{person ...}} tags rather than nouns and names, so the
        // words are chosen here at render time and a rename reaches rows already written (D-027,
        // contact rule 8). An edit's line adds the labels of the fields it changed, which open a
        // card of each change before and after (D-151 rule 5, amended). The title is the lead as
        // plain text, a person by their display name, looked up with the page of rows.
        displayValue: (_table: DataTable, item: SecurityAuditLogItem) =>
          html`<span
            class="description-cell"
            title=${keptTextPlain(item.description)}
            ><security-audit-change-line
              .lead=${item.description}
              .changes=${item.changes ?? []}
            ></security-audit-change-line
          ></span>`,
      },
      {
        title: terms.loginGroup.value,
        fieldName: 'groupName',
        width: '150px',
        displayValue: (_table: DataTable, item: SecurityAuditLogItem) =>
          html`${item.groupName ?? '-'}`,
      },
    ];
  }

  private renderCategoryBadge(category: string) {
    const variantMap: Record<string, string> = {
      User: 'primary',
      Group: 'success',
      Site: 'warning',
      Role: 'neutral',
      Assignment: 'danger',
    };
    const variant = variantMap[category] ?? 'neutral';
    return html`<sl-badge variant=${variant} pill
      >${auditCategoryLabel(category)}</sl-badge
    >`;
  }

  private renderActionBadge(actionType: string) {
    const variantMap: Record<string, string> = {
      Created: 'success',
      Updated: 'primary',
      Deleted: 'danger',
      Locked: 'warning',
      Activated: 'success',
      Assigned: 'primary',
      Unassigned: 'neutral',
      PasswordReset: 'warning',
    };
    const variant = variantMap[actionType] ?? 'neutral';
    return html`<sl-badge variant=${variant} pill>${actionType}</sl-badge>`;
  }

  private createAuditProvider() {
    this.auditProvider = async (request) => {
      this.error = undefined;

      try {
        // One descriptor per ticked value; the job treats a repeated field as any-of.
        const filters: DataTableFilterDescriptor<string>[] = [];
        for (const facet of [FACET_CATEGORY, FACET_ACTIVITY, FACET_ACTION]) {
          for (const value of selectedValues(this.filterSelection, facet)) {
            filters.push({ field: facet, operator: 'eq', value });
          }
        }

        const api = ApiFactory.authenticated(ApiSecurityAudit);
        const res = await api.searchAuditLogs({
          sliceStart: request.sliceStart ?? 0,
          sliceLength: request.sliceLength ?? 100,
          filters: filters as unknown as ApiFilterDescriptor[],
          freeText: this.searchText || undefined,
          // THE TABLE IS THE ONLY PLACE THE ORDER IS STATED. This used to substitute `createdUtc`
          // descending whenever the request carried none, which is a second copy of an intent in
          // a place no screen reflects - exactly what hid the dead `.sortDirection` binding in the
          // markup below for as long as it did. The table now says it, and only the table.
          sort: request.sort.map((s) => ({
            field: s.field,
            direction: s.direction === 'desc' ? 'Desc' : 'Asc',
          })),
          requiresCount: request.sliceStart === 0,
        });

        if (!res.success) {
          throw new Error(res.error || 'Failed to load audit logs');
        }

        const items = res.data?.items ?? [];
        // The people the page's descriptions name by reference, looked up before the rows render,
        // so each cell's plain-text title names them rather than "a <user>" (U47 review 2 R2): the
        // title is fixed when the cell renders, and only <user-name> follows the directory after.
        // A lookup that fails costs only the title's name, never the page.
        await userDirectoryInstance()
          .preFetch(items.flatMap((item) => personIdsIn(item.description)))
          .catch(() => undefined);
        this.hasItems = items.length > 0 || (request.sliceStart ?? 0) > 0;
        this.hasFetched = true;
        if ((request.sliceStart ?? 0) === 0) {
          this.matchedCount = res.data?.totalCount ?? null;
        }

        return {
          items,
          totalCount: res.data?.totalCount,
          hasMore: res.data?.hasMore ?? false,
        };
      } catch (e) {
        this.hasFetched = true;
        this.error = (e as Error).message || 'Failed to load audit logs';
        throw e;
      }
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.createAuditProvider();
  }

  /**
   * A new provider makes the table refetch from the first page with the current query.
   *
   * The fetched flags are reset FIRST, because an empty result swaps the table for the
   * empty state, and a table that is not on the page cannot refetch: ticking a category
   * with no rows and unticking it again left "0 entries" on screen for good (architect,
   * 2026-09-06, the Role category). Resetting puts the table back.
   *
   * The table fetches by itself, once: a table already on the page reloads when its provider
   * changes, and a table put back fetches when it first renders (data-table.ts `updated` and
   * `firstUpdated`). Asking it to fetch as well sent every search and filter change to the server
   * twice (#398 C14 e).
   */
  private applyFilters() {
    this.hasFetched = false;
    this.hasItems = false;
    this.matchedCount = null;
    this.createAuditProvider();
  }

  private handleFilterChange(e: CustomEvent<{ selection: FilterSelection }>) {
    this.filterSelection = e.detail.selection;
    this.applyFilters();
  }

  /** Everything narrowing the list is undone at once - the ticks AND the search box. */
  private handleFilterClear() {
    this.filterSelection = {};
    this.searchText = '';
    this.applyFilters();
  }

  private handleFilterDrop(e: CustomEvent<{ key: string }>) {
    if (e.detail.key === SEARCH_CLAUSE_KEY) {
      this.searchText = '';
    } else {
      this.filterSelection = { ...this.filterSelection, [e.detail.key]: [] };
    }
    this.applyFilters();
  }

  /**
   * Runs the search the box reports, once. The box reports one search twice - `search-input` as the
   * person types and `search-change` when they press Enter or clear - so a value already searched is
   * not searched again (#398 C14 e). A clear is reported as `search-clear` and then a change to
   * nothing, so the change finds the box already empty and is the one dropped.
   */
  private handleSearchInput(e: CustomEvent<{ value: string }>) {
    if (e.detail.value === this.searchText) return;
    this.searchText = e.detail.value;
    this.applyFilters();
  }

  private handleSearchClear() {
    this.searchText = '';
    this.applyFilters();
  }

  private get anyFilterApplied(): boolean {
    return (
      this.searchText.trim() !== '' || !isSelectionEmpty(this.filterSelection)
    );
  }

  /**
   * The facets, in the order the old dropdowns stood. Values are the audit rows' own codes
   * (the server compares them as written); labels are what the old options showed.
   */
  private get filterFacets(): FilterFacet[] {
    const plain = (values: string[]) =>
      values.map((value) => ({ value, label: value }));
    return [
      {
        key: FACET_CATEGORY,
        title: 'Category',
        phrase: 'about',
        // Every AuditCategory the server can write, guarded by
        // SecurityAuditFieldLabelCoverageTests (#398 C17b): Domain was missing. Labelled by
        // auditCategoryLabel, as each row's badge is, so the two read the same word (review 2 N1).
        options: ['User', 'Group', 'Site', 'Role', 'Assignment', 'Domain'].map(
          (value) => ({ value, label: auditCategoryLabel(value) })
        ),
      },
      {
        key: FACET_ACTIVITY,
        title: 'Activity',
        phrase: 'recording',
        options: [
          { value: 'AddUser', label: `Add ${terms.loginUser.value}` },
          { value: 'EditUser', label: `Edit ${terms.loginUser.value}` },
          { value: 'DeleteUser', label: `Delete ${terms.loginUser.value}` },
          { value: 'ResetPassword', label: 'Reset Password' },
          { value: 'AddGroup', label: `Add ${terms.loginGroup.value}` },
          { value: 'EditGroup', label: `Edit ${terms.loginGroup.value}` },
          { value: 'AddSite', label: `Add ${terms.loginSite.value}` },
          { value: 'EditSite', label: `Edit ${terms.loginSite.value}` },
          { value: 'DeleteSite', label: `Delete ${terms.loginSite.value}` },
          {
            value: 'AssignUserToSite',
            label: `Assign to ${terms.loginSite.value}`,
          },
          {
            value: 'RemoveUserFromSite',
            label: `Remove from ${terms.loginSite.value}`,
          },
          { value: 'AssignRole', label: 'Assign Role' },
          { value: 'RemoveRole', label: 'Remove Role' },
          { value: 'EditRoleSet', label: 'Edit Role Set' },
          // The older activities the filter had left out (#398 C17b, 3(c)). Every AuditActivity
          // value has an entry here, guarded by SecurityAuditFieldLabelCoverageTests, so an entry the
          // server writes can always be chosen. LockUser, UnlockUser and DeleteGroup are declared
          // but written by no job today; they are offered so the guard can hold for every value.
          { value: 'LockUser', label: `Block ${terms.loginUser.value}` },
          { value: 'UnlockUser', label: `Activate ${terms.loginUser.value}` },
          { value: 'DeleteGroup', label: `Delete ${terms.loginGroup.value}` },
          {
            value: 'SetSiteOwner',
            label: `Set ${terms.loginSite.value} ${terms.siteOwner.value}`,
          },
          {
            value: 'UpdateSiteAssignment',
            label: `Update ${terms.loginSite.value} Assignment`,
          },
          {
            value: 'AttachDomain',
            label: `Add ${terms.userGroupDomain.value}`,
          },
          {
            value: 'DeactivateDomain',
            label: `Deactivate ${terms.userGroupDomain.value}`,
          },
          {
            value: 'ReactivateDomain',
            label: `Reactivate ${terms.userGroupDomain.value}`,
          },
          {
            value: 'HardDeleteDomain',
            label: `Remove ${terms.userGroupDomain.value}`,
          },
          // The Glass Accreditation assignment changes (D-166).
          {
            value: 'AddGlassDataEntryAssignment',
            label: 'Add Data Entry Access',
          },
          {
            value: 'RemoveGlassDataEntryAssignment',
            label: 'Remove Data Entry Access',
          },
          {
            value: 'ToggleGlassDataEntryAllSites',
            label: `Data Entry All ${terms.loginSite.plural}`,
          },
          {
            value: 'UpdateGlassDataEntryNotifications',
            label: 'Data Entry Notifications',
          },
          {
            value: 'AddGlassAuditorAssignment',
            label: `Add ${terms.auditor.value} Access`,
          },
          {
            value: 'RemoveGlassAuditorAssignment',
            label: `Remove ${terms.auditor.value} Access`,
          },
          {
            value: 'ToggleGlassAuditorAllSites',
            label: `${terms.auditor.value} All ${terms.loginSite.plural}`,
          },
          {
            value: 'UpdateGlassAuditorNotifications',
            label: `${terms.auditor.value} Notifications`,
          },
        ],
      },
      {
        key: FACET_ACTION,
        title: 'Action',
        phrase: 'whose action is',
        options: [
          // Every AuditActionType, guarded as the activities are (#398 C17b): Locked, Activated,
          // Attached, Deactivated and Reactivated were missing.
          ...plain([
            'Created',
            'Updated',
            'Deleted',
            'Locked',
            'Activated',
            'Assigned',
            'Unassigned',
            'Attached',
            'Deactivated',
            'Reactivated',
          ]),
          { value: 'PasswordReset', label: 'Password Reset' },
        ],
      },
    ];
  }

  render() {
    const showEmpty = this.hasFetched && !this.hasItems && !this.error;

    // No page title: the app header already carries it (architect, 2026-09-06). The search
    // sits alone above; everything that narrows rows is in the rail beside the table.
    return html`
      <div class="audit-page">
        <!--
          The search row and the list are ONE block that can take the whole viewport in place
          (D-106): searching while scanning the rows is the common case, so the row travels with
          the rail and the table rather than being left behind on a page nobody can see.
        -->
        <agwa-fullscreen-block .title=${'Security Audit Log'}>
          <div class="filters" slot="header">
            <agwa-search
              size="small"
              .value=${this.searchText}
              placeholder="Search..."
              @search-input=${this.handleSearchInput}
              @search-change=${this.handleSearchInput}
              @search-clear=${this.handleSearchClear}
            ></agwa-search>
            ${this.error ? html`<span class="error">${this.error}</span>` : nothing}
          </div>
          <div class="list-body">
            <agwa-filter-rail
              storage-key="security-audit-log"
              .facets=${this.filterFacets}
              .selection=${this.filterSelection}
              ?can-clear=${this.anyFilterApplied}
              @filter-change=${this.handleFilterChange}
              @filter-clear=${this.handleFilterClear}
            ></agwa-filter-rail>
            <div class="list-main">
              <agwa-filter-summary
                .facets=${this.filterFacets}
                .selection=${this.filterSelection}
                search-text=${this.searchText}
                subject="audit entries"
                .shownCount=${this.matchedCount ?? 0}
                .totalCount=${null}
                @filter-drop=${this.handleFilterDrop}
              ></agwa-filter-summary>
              ${
                showEmpty
                  ? html`<agwa-empty-state
                      icon="shield-check"
                      title="No audit logs"
                      message="No security audit logs found matching your criteria."
                    ></agwa-empty-state>`
                  : this.auditProvider
                    ? // NEWEST FIRST. A person opens an audit log to see what just happened, and
                      // the log only ever grows, so opening on the oldest row means scrolling past
                      // the whole history to reach the reason they came. The property is
                      // `sortAscending`, and the table reads undefined as ASCENDING, so the
                      // descending order is stated rather than left to a default. It replaces a
                      // `.sortDirection` binding that named no property of the table and did
                      // nothing, which is why this log opened on the oldest entry.
                      html`<agwa-lit-table
                        fill-height
                        .columns=${this.auditColumns}
                        .dataProvider=${this.auditProvider}
                        .pageLength=${100}
                        .showLoadMore=${true}
                        sortField="createdUtc"
                        .sortAscending=${false}
                      ></agwa-lit-table>`
                    : html``
              }
            </div>
          </div>
        </agwa-fullscreen-block>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'security-audit-log': SecurityAuditLog;
  }
}
